import { InvoiceData, PrintSettings, DispatchPrintVariant } from '@/types/invoice';
import { TabItem } from '@/types';
import { PrinterTemplate, PrinterConfig } from '../types';
import { format } from 'date-fns';
import { COLONES_PAYMENT_METHOD, DOLLAR_PAYMENT_METHOD } from '@/constants/defaultPaymentMethodContant';
import { formatCurrency } from '@/lib/currency';
import { useOrganizationStore } from '@/stores/organizationStore';
import { calculateTabDiscountAmount } from '@/utils/tabTotals';
import { formatWeight, isWeightLine } from '@/utils/weightPricing';

// blank lines before the final cut
const TICKET_TRAILING_FEED_LINES = 3;

/** Short, playful kitchen-ticket footers (max 40 chars each). */
const KITCHEN_DISPATCH_PHRASES = [
  'Recordá divertirte.',
  'Que fluya la cocina.',
  'A darle con sabor.',
  'Hoy se cocina con estilo.',
  'No te quemes... mucho.',
  'Pedidos con buena vibra.',
  'A cocinar como campeón.',
  '¡Hazlo rico, hazlo rico!',
  'Más fuego, menos drama.',
  'Cocina feliz, vive feliz.',
  'Que no se enfríe el ánimo.',
  'Plato listo, sonrisa lista',
  'El hambre no espera.',
  'Brilla como ese sartén.',
  'Servicio con chispa.',
  'A fuego lento... o no',
  'Hoy toca ser leyenda.',
  '¡Sazón al máximo!',
  'Que se note el talento.',
  'Cada plato cuenta una historia.',
  'Cada segundo cuenta.',
  '¡No te duermas, crack!',
  '¡Dale gas sin miedo!',
  '¡Que arda esa cocina!',
  'Sin drama, con sazón.',
  '¡A matar el hambre!',
  'Métale ganas.',
  'Arte en cada sartén.',
  'Chef mode: activado.',
  'Que hable el sabor.',
  'Ritmo, fuego y flow.',
  'Esto no es cocina: es show.',
  'Plato con actitud.',
  'Sazón, estilo y cero miedo.',
  'Hoy se cocina en grande.',
  '¿Alguien lee esto?',
  'No se distraiga leyendo esto.',
  'No leer esta línea.',
  'El que lee esta línea invita a las birras',
] as const;

const getRandomKitchenPhrase = (): string => {
  const index = Math.floor(Math.random() * KITCHEN_DISPATCH_PHRASES.length);
  return KITCHEN_DISPATCH_PHRASES[index];
};

const appendTrailingFeed = (commands: Array<{ method: string; params: unknown[] }>) => {
  commands.push({ method: 'addFeedLine', params: [TICKET_TRAILING_FEED_LINES] });
};

/**
 * Get default print settings for Epson thermal printer
 */
const getDefaultSettings = (): PrintSettings => {
  return {
    paperWidth: 80,
    fontSize: {
      header: 14,
      normal: 10,
      small: 8,
    },
    margins: {
      top: 5,
      bottom: 5,
      left: 5,
      right: 5,
    },
    includeQRCode: false,
    includeLogo: false,
    language: 'es',
    currency: 'USD',
  };
};

// Create a reusable header that can be used in multiple templates
const createHeader = (invoiceData: InvoiceData, isPaymentReceipt: boolean): Array<{ method: string; params: unknown[] }> => {
  const commands: Array<{ method: string; params: unknown[] }> = [];

  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  commands.push({ method: 'addText', params: [`${invoiceData.organizationName.toUpperCase()}\n`] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  if (invoiceData.organizationSlogan) commands.push({ method: 'addText', params: [`${invoiceData.organizationSlogan}\n`] });
  if (invoiceData.organizationAddress) commands.push({ method: 'addText', params: [`${invoiceData.organizationAddress}\n`] });
  if (invoiceData.organizationPhone) commands.push({ method: 'addText', params: [`Teléfono: ${invoiceData.organizationPhone}\n`] });
  if (invoiceData.organizationLegalName) {
    wrapThermalText(invoiceData.organizationLegalName).forEach((line) => {
      commands.push({ method: 'addText', params: [`${line}\n`] });
    });
  }
  if (invoiceData.organizationLegalId) commands.push({ method: 'addText', params: [`Cedula: ${invoiceData.organizationLegalId}\n`] });
  commands.push({ method: 'addFeedLine', params: [2] });
  //TODO: Translate
  if (!isPaymentReceipt) commands.push({ method: 'addText', params: ['Cuenta Pendiente \n'] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addTextAlign', params: ['left'] });
  commands.push({ method: 'addText', params: [`Fecha: ${format(invoiceData.createdAt, 'dd/MM/yyyy HH:mm')}\n`] });
  if (invoiceData.tableName) {
    commands.push({ method: 'addText', params: [`Mesa: ${invoiceData.tableName}\n`] });
  }
  commands.push({ method: 'addText', params: [`Cliente: ${invoiceData.customerName}\n`] });
  if (invoiceData.serverName) {
    commands.push({ method: 'addText', params: [`Salonero: ${invoiceData.serverName}\n`] });
  }
  const facturaReference = resolveFacturaReference(invoiceData);
  if (facturaReference) {
    commands.push({ method: 'addText', params: [`Factura: ${facturaReference}\n`] });
  }
  if (isPaymentReceipt && invoiceData.tabId) {
    commands.push({ method: 'addText', params: [`${formatOrderNumberLabel(invoiceData.tabId, 'Orden')}\n`] });
  }
  if (invoiceData.electronicInvoiceClave) {
    commands.push({ method: 'addText', params: ['Clave:\n'] });
    wrapThermalText(invoiceData.electronicInvoiceClave).forEach((line) => {
      commands.push({ method: 'addText', params: [`${line}\n`] });
    });
  }

  return commands;
};

const formatPrice = (price: number, currency: string): string => {
  return formatCurrency(price, currency).replace(/₡/g, '¢');
};

const maxLineLength = 48; // 48 characters per line for epson printer 80mm
const DEFAULT_AMOUNT_RIGHT_OFFSET = 4; // shift price/total columns left from paper edge
// TODO: remove Sibu Escaleras printer workaround once that device's right-edge clipping is fixed
const SIBU_ESCALERAS_SLUG = 'sibu-escaleras';
const SIBU_ESCALERAS_AMOUNT_RIGHT_OFFSET = 6;

const getAmountColumnWidth = (): number => {
  const slug = useOrganizationStore.getState().currentOrganizationSlug;
  const offset =
    slug === SIBU_ESCALERAS_SLUG ? SIBU_ESCALERAS_AMOUNT_RIGHT_OFFSET : DEFAULT_AMOUNT_RIGHT_OFFSET;
  return maxLineLength - offset;
};

const wrapThermalText = (text: string, maxLen = maxLineLength): string[] => {
  if (text.length <= maxLen) {
    return [text];
  }

  const lines: string[] = [];
  for (let index = 0; index < text.length; index += maxLen) {
    lines.push(text.slice(index, index + maxLen));
  }
  return lines;
};

const resolveFacturaReference = (invoiceData: InvoiceData): string => {
  const reference = invoiceData.id?.trim();
  if (reference) {
    return reference;
  }

  return invoiceData.tabId ? invoiceData.tabId.slice(-8) : '';
};

const formatOrderNumberLabel = (tabId: string, label: 'ORDEN' | 'Orden' = 'ORDEN'): string =>
  `${label}: #${tabId.slice(-3).toUpperCase()}`;

type ThermalCommand = { method: string; params: unknown[] };

const getThermalSeparator = () => `${'='.repeat(getAmountColumnWidth())}\n`;
const getThermalDashLine = () => `${'-'.repeat(getAmountColumnWidth())}\n`;

const padLine = (leftText: string, rightText: string) =>
  `${leftText.padEnd(maxLineLength - rightText.length, ' ')}${rightText}`;

const padAmountLine = (leftText: string, rightText: string) => {
  const amountColumnWidth = getAmountColumnWidth();
  return `${leftText.padEnd(amountColumnWidth - rightText.length, ' ')}${rightText}`;
};

const appendReportHeader = (
  commands: ThermalCommand[],
  {
    title,
    organizationName,
    detailLines,
  }: {
    title: string;
    organizationName: string;
    detailLines: string[];
  },
) => {
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  commands.push({ method: 'addText', params: [`${title}\n`] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addText', params: [`${organizationName.toUpperCase()}\n`] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addTextAlign', params: ['left'] });
  detailLines.forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  commands.push({ method: 'addText', params: [getThermalSeparator()] });
};

const appendReportFooter = (commands: ThermalCommand[]) => {
  commands.push({ method: 'addText', params: [getThermalSeparator()] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_b'] });
  commands.push({ method: 'addText', params: ['Powered by Hermosa Software\n'] });
  appendTrailingFeed(commands);
};

const formatItem = (item, currency) => {
  const itemTotal = item.unitPrice * item.quantity * (1 - item.discount / 100);
  const productName = item.product.name.length > 30 ?
    item.product.name.substring(0, 17) + '...' :
    item.product.name.padEnd(10);
  const hasWeight = isWeightLine(item);
  const quantity = hasWeight
    ? `${formatWeight(item.weightKg!)} `.padEnd(3)
    : item.quantity.toString().padEnd(3);
  const total = formatPrice(itemTotal, currency);

  // format the line to be printed in the receipt
  const remainingSpaces = getAmountColumnWidth() - (quantity.length + total.length);

  return `${quantity}${productName.padEnd(remainingSpaces)}${total}`;
}

interface DispatchOrderData {
  organizationName: string;
  tabId: string;
  customerName: string;
  tableName?: string;
  createdAt: Date;
  items: TabItem[];
  serverName?: string;
}

export interface CashBalancePrintData {
  date: string;
  organizationName: string;
  userName: string;
  expectedCash: number;
  actualCash: number;
  cashDifference: number;
  expectedCard: number;
  actualCard: number;
  cardDifference: number;
  notes?: string;
  withdrawals?: number;
  /** Float carried in from the previous balanced day. */
  openingCash?: number;
  /** Float left in the register for the next day. */
  startingCash?: number;
  dollarCashReceived?: number;
  exchangeRate?: number;
  referencePayments?: Array<{ name: string; amount: number; excludedFromRevenue?: boolean }>;
  serviceChargeTotal?: number;
  ivaTotal?: number;
  serviceChargeRate?: number;
  ivaRate?: number;
  showExpectedCash?: boolean;
}

export interface CashWithdrawalPrintData {
  organizationName: string;
  userName: string;
  amount: number;
  justification: string;
  date: string;
  withdrawnAt: Date;
}

const formatDifference = (amount: number, currency: string): string => {
  const prefix = amount > 0 ? '+' : '';
  return `${prefix}${formatPrice(amount, currency)}`;
};

const appendPaymentMethodSection = (
  commands: Array<{ method: string; params: unknown[] }>,
  label: string,
  expected: number,
  declared: number | null,
  difference: number | null,
  currency: string,
  referenceNote = 'Solo referencia',
  showExpected = true,
) => {
  commands.push({ method: 'addText', params: [`${label}\n`] });
  if (showExpected) {
    commands.push({ method: 'addText', params: [`  Esperado: ${formatPrice(expected, currency)}\n`] });
  } else if (declared == null) {
    commands.push({ method: 'addText', params: [`  Monto: ${formatPrice(expected, currency)}\n`] });
  }
  if (declared != null) {
    commands.push({ method: 'addText', params: [`  Declarado: ${formatPrice(declared, currency)}\n`] });
  }
  if (showExpected) {
    if (difference != null) {
      commands.push({ method: 'addText', params: [`  Diferencia: ${formatDifference(difference, currency)}\n`] });
    } else {
      commands.push({ method: 'addText', params: [`  ${referenceNote}\n`] });
    }
  } else if (declared == null) {
    commands.push({ method: 'addText', params: [`  ${referenceNote}\n`] });
  }
  commands.push({ method: 'addText', params: [getThermalDashLine()] });
};

/**
 * Create cash balance closing template for Epson printer
 */
export const createCashBalanceTemplate = (
  data: CashBalancePrintData,
  config: PrinterConfig,
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const currency = config.currency || 'CRC';
  const balanceDate = new Date(`${data.date}T12:00:00`);
  const totalExpected = data.expectedCash + data.expectedCard;
  const totalActual = data.actualCash + data.actualCard;
  const totalDifference = data.cashDifference + data.cardDifference;
  const showExpectedCash = data.showExpectedCash !== false;

  appendReportHeader(commands, {
    title: 'CIERRE DE CAJA',
    organizationName: data.organizationName,
    detailLines: [
      `Fecha: ${format(balanceDate, 'dd/MM/yyyy')}`,
      `Realizado por: ${data.userName}`,
    ],
  });

  appendPaymentMethodSection(
    commands,
    'Efectivo',
    data.expectedCash,
    data.actualCash,
    data.cashDifference,
    currency,
    undefined,
    showExpectedCash,
  );
  appendPaymentMethodSection(
    commands,
    'Tarjeta',
    data.expectedCard,
    data.actualCard,
    data.cardDifference,
    currency,
    undefined,
    showExpectedCash,
  );

  for (const payment of data.referencePayments ?? []) {
    if (!payment?.amount || payment.amount <= 0) continue;
    appendPaymentMethodSection(
      commands,
      payment.name,
      payment.amount,
      null,
      null,
      currency,
      payment.excludedFromRevenue
        ? 'Solo referencia (no cuenta en ventas)'
        : 'Solo referencia',
      showExpectedCash,
    );
  }

  commands.push({ method: 'addTextSize', params: [1, 2] });
  commands.push({ method: 'addText', params: ['TOTAL\n'] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  if (showExpectedCash) {
    commands.push({ method: 'addText', params: [`  Esperado: ${formatPrice(totalExpected, currency)}\n`] });
  }
  commands.push({ method: 'addText', params: [`  Declarado: ${formatPrice(totalActual, currency)}\n`] });
  if (showExpectedCash) {
    commands.push({ method: 'addText', params: [`  Diferencia: ${formatDifference(totalDifference, currency)}\n`] });
  }
  commands.push({ method: 'addText', params: [getThermalDashLine()] });

  if ((data.serviceChargeTotal ?? 0) > 0 || (data.ivaTotal ?? 0) > 0) {
    commands.push({ method: 'addText', params: ['RESUMEN DE IMPUESTOS\n'] });
    if ((data.serviceChargeTotal ?? 0) > 0) {
      const serviceLabel = data.serviceChargeRate
        ? `Servicio ${data.serviceChargeRate}%`
        : 'Servicio';
      commands.push({
        method: 'addText',
        params: [padLine(serviceLabel, formatPrice(data.serviceChargeTotal!, currency)) + '\n'],
      });
    }
    if ((data.ivaTotal ?? 0) > 0) {
      const ivaLabel = data.ivaRate ? `IVA ${data.ivaRate}%` : 'IVA';
      commands.push({
        method: 'addText',
        params: [padLine(ivaLabel, formatPrice(data.ivaTotal!, currency)) + '\n'],
      });
    }
    commands.push({ method: 'addText', params: [getThermalDashLine()] });
  }

  if (data.openingCash && data.openingCash > 0) {
    commands.push({ method: 'addText', params: [padLine('Caja inicial', formatPrice(data.openingCash, currency)) + '\n'] });
  }

  if (data.withdrawals && data.withdrawals > 0) {
    commands.push({ method: 'addText', params: [padLine('Retiros realizados', formatPrice(data.withdrawals, currency)) + '\n'] });
  }

  if (data.startingCash != null && data.startingCash > 0) {
    commands.push({ method: 'addText', params: [padLine('Se deja en caja', formatPrice(data.startingCash, currency)) + '\n'] });
    const netToDeposit = data.actualCash - data.startingCash;
    if (netToDeposit >= 0) {
      commands.push({
        method: 'addText',
        params: [padLine('A depositar', formatPrice(netToDeposit, currency)) + '\n'],
      });
    } else {
      commands.push({
        method: 'addText',
        params: [padLine('Aporte a caja', formatPrice(-netToDeposit, currency)) + '\n'],
      });
    }
  }

  if (data.dollarCashReceived && data.dollarCashReceived > 0) {
    commands.push({
      method: 'addText',
      params: [padLine('Dolares en efectivo', `$${data.dollarCashReceived.toFixed(2)}`) + '\n'],
    });
    if (data.exchangeRate) {
      commands.push({
        method: 'addText',
        params: [padLine('Tipo de cambio', data.exchangeRate.toFixed(2)) + '\n'],
      });
    }
  }

  if (data.notes?.trim()) {
    commands.push({ method: 'addText', params: [`Notas: ${data.notes.trim()}\n`] });
  }

  appendReportFooter(commands);

  return {
    commands,
    metadata: {
      type: 'cash_balance',
      timestamp: new Date(),
    },
  };
};

/**
 * Create cash withdrawal template for Epson printer
 */
export const createCashWithdrawalTemplate = (
  data: CashWithdrawalPrintData,
  config: PrinterConfig,
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const currency = config.currency || 'CRC';
  const withdrawalDate = new Date(`${data.date}T12:00:00`);

  appendReportHeader(commands, {
    title: 'RETIRO DE CAJA',
    organizationName: data.organizationName,
    detailLines: [
      `Fecha: ${format(withdrawalDate, 'dd/MM/yyyy')}`,
      `Hora: ${format(data.withdrawnAt, 'HH:mm')}`,
      `Retirado por: ${data.userName}`,
    ],
  });

  commands.push({ method: 'addTextSize', params: [1, 2] });
  commands.push({
    method: 'addText',
    params: [`${padLine('Monto', formatPrice(data.amount, currency))}\n`],
  });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addText', params: [getThermalDashLine()] });
  commands.push({ method: 'addText', params: [`Justificacion: ${data.justification.trim()}\n`] });

  appendReportFooter(commands);

  return {
    commands,
    metadata: {
      type: 'cash_withdrawal',
      timestamp: new Date(),
    },
  };
};

const appendDispatchItems = (commands: ThermalCommand[], items: TabItem[]) => {
  items.forEach((item) => {
    const itemName = item.product.name || 'Producto';
    const itemNotes = item.notes?.trim();
    const hasWeight = isWeightLine(item);
    const qtyLabel = hasWeight
      ? formatWeight(item.weightKg!)
      : `${item.quantity} x`;

    commands.push({ method: 'addTextSize', params: [1, 2] });
    commands.push({ method: 'addText', params: [`${qtyLabel} ${itemName}\n`] });
    commands.push({ method: 'addTextSize', params: [1, 1] });

    const categoryName = item.product.productCategory?.name?.trim();
    if (categoryName) {
      commands.push({ method: 'addText', params: [`   Categoria: ${categoryName}\n`] });
    }

    for (const modifier of item.modifiers ?? []) {
      commands.push({ method: 'addText', params: [`   + ${modifier.name}\n`] });
    }

    if (item.promotionComponents?.length) {
      for (const comp of item.promotionComponents) {
        const compQty = comp.quantity * item.quantity;
        commands.push({
          method: 'addText',
          params: [`   - ${comp.productName} x${compQty}\n`],
        });
      }
    }

    if (itemNotes) {
      commands.push({ method: 'addText', params: [`   Nota: ${itemNotes}\n`] });
    }
    if (item.customerPosition) {
      commands.push({ method: 'addText', params: [`   Puesto: ${item.customerPosition}\n`] });
    }
    commands.push({ method: 'addFeedLine', params: [1] });
  });
};

const appendDispatchTicketHeader = (
  commands: ThermalCommand[],
  headerLabel: string,
  dispatchData: Omit<DispatchOrderData, 'items'>,
) => {
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  commands.push({ method: 'addText', params: [`${headerLabel}\n`] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  commands.push({ method: 'addText', params: [`${dispatchData.organizationName.toUpperCase()}\n`] });
  commands.push({ method: 'addFeedLine', params: [1] });

  commands.push({ method: 'addTextSize', params: [2, 2] });
  commands.push({ method: 'addText', params: [`${formatOrderNumberLabel(dispatchData.tabId)}\n`] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addFeedLine', params: [1] });

  commands.push({ method: 'addTextAlign', params: ['left'] });
  if (dispatchData.tableName) {
    commands.push({ method: 'addText', params: [`Mesa: ${dispatchData.tableName}\n`] });
  }
  commands.push({ method: 'addText', params: [`Cliente: ${dispatchData.customerName}\n`] });
  if (dispatchData.serverName) {
    commands.push({ method: 'addText', params: [`Salonero: ${dispatchData.serverName}\n`] });
  }
  commands.push({ method: 'addText', params: [`Fecha: ${format(dispatchData.createdAt, 'dd/MM/yyyy HH:mm')}\n`] });
  commands.push({ method: 'addText', params: [getThermalDashLine()] });
};

/**
 * Create dispatch order template for Epson printer (kitchen ticket)
 */
export const createDispatchOrderTemplate = (
  dispatchData: DispatchOrderData,
  area: 'cocina' | 'bar',
  variant: DispatchPrintVariant = 'order',
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const headerLabel = variant === 'cancellation'
    ? `CANCELACION DE ${area.toUpperCase()}`
    : `ORDEN DE ${area.toUpperCase()}`;

  appendDispatchTicketHeader(commands, headerLabel, dispatchData);
  appendDispatchItems(commands, dispatchData.items);

  commands.push({ method: 'addFeedLine', params: [2] });
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_b'] });
  commands.push({ method: 'addText', params: [`${getRandomKitchenPhrase()}\n`] });
  appendTrailingFeed(commands);

  return {
    commands,
    metadata: {
      type: variant === 'cancellation' ? 'dispatch_cancellation' : 'dispatch_order',
      timestamp: new Date(),
    },
  };
};

/**
 * One cashier ticket that copies kitchen and bar dispatch items together.
 */
export const createCashierDispatchCopyTemplate = (
  dispatchData: Omit<DispatchOrderData, 'items'>,
  sections: { kitchen: TabItem[]; bar: TabItem[] },
  variant: DispatchPrintVariant = 'order',
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const headerLabel = variant === 'cancellation' ? 'COPIA DE CANCELACION' : 'COPIA DE COMANDA';

  appendDispatchTicketHeader(commands, headerLabel, dispatchData);

  if (sections.kitchen.length > 0) {
    commands.push({ method: 'addTextAlign', params: ['center'] });
    commands.push({ method: 'addTextSize', params: [2, 1] });
    commands.push({ method: 'addText', params: ['COCINA\n'] });
    commands.push({ method: 'addTextSize', params: [1, 1] });
    commands.push({ method: 'addTextAlign', params: ['left'] });
    appendDispatchItems(commands, sections.kitchen);
  }

  if (sections.bar.length > 0) {
    commands.push({ method: 'addTextAlign', params: ['center'] });
    commands.push({ method: 'addTextSize', params: [2, 1] });
    commands.push({ method: 'addText', params: ['BAR\n'] });
    commands.push({ method: 'addTextSize', params: [1, 1] });
    commands.push({ method: 'addTextAlign', params: ['left'] });
    appendDispatchItems(commands, sections.bar);
  }

  commands.push({ method: 'addFeedLine', params: [2] });
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_b'] });
  commands.push({ method: 'addText', params: [`${getRandomKitchenPhrase()}\n`] });
  appendTrailingFeed(commands);

  return {
    commands,
    metadata: {
      type: variant === 'cancellation' ? 'dispatch_cancellation' : 'dispatch_order',
      timestamp: new Date(),
    },
  };
};

/**
 * Create tab invoice template for Epson printer
 */
export const createTabInvoiceTemplate = (
  invoiceData: InvoiceData,
  config: PrinterConfig,
): PrinterTemplate => {

  const finalSettings = { ...getDefaultSettings(), ...config };
  const commands: Array<{ method: string; params: unknown[] }> = [];
  const isPaymentReceipt = !invoiceData.forPreview;

  // Header
  const header = createHeader(invoiceData, isPaymentReceipt);
  const combinedCommands = commands.concat(header);
  // Body
  combinedCommands.push({ method: 'addText', params: [getThermalSeparator()] });
  // Items
  for (const item of invoiceData.items) {
    const itemLine = formatItem(item, finalSettings.currency);
    combinedCommands.push({ method: 'addText', params: [`${itemLine}\n`] });

    for (const modifier of item.modifiers ?? []) {
      combinedCommands.push({ method: 'addText', params: [`  + ${modifier.name}\n`] });
    }

    if (item.discount > 0) {
      combinedCommands.push({ method: 'addText', params: [`  Desc. ${item.discount}% \n`] });
    }
  }
  // Footer
  combinedCommands.push({ method: 'addText', params: [getThermalDashLine()] });
  combinedCommands.push({ method: 'addTextAlign', params: ['left'] });
  combinedCommands.push({ method: 'addText', params: [`${padAmountLine('Subtotal', formatPrice(invoiceData.subtotal, finalSettings.currency))}\n`] });
  const tabDiscountAmount = calculateTabDiscountAmount({
    discount: invoiceData.discount,
    items: invoiceData.items,
  });
  if (tabDiscountAmount > 0) {
    combinedCommands.push({
      method: 'addText',
      params: [`${padAmountLine(`Descuento ${invoiceData.discount}%`, formatPrice(tabDiscountAmount, finalSettings.currency))}\n`],
    });
  }
  // Add Service Charge if enabled
  if (invoiceData.serviceCharge && invoiceData.serviceCharge > 0) {
    combinedCommands.push({ method: 'addText', params: [`${padAmountLine(`Servicio ${invoiceData.serviceChargeRate}%`, formatPrice(invoiceData.serviceCharge, finalSettings.currency))}\n`] });
  }
  // Add IVA if enabled
  if (invoiceData.ivaCharge && invoiceData.ivaCharge > 0) {
    combinedCommands.push({ method: 'addText', params: [`${padAmountLine(`IVA ${invoiceData.ivaRate}%`, formatPrice(invoiceData.ivaCharge, finalSettings.currency))}\n`] });
  }
  // Add Tip/Propina if present
  const tip = invoiceData.tip ?? 0;
  if (tip > 0) {
    combinedCommands.push({
      method: 'addText',
      params: [`${padAmountLine('Propina', formatPrice(tip, finalSettings.currency))}\n`],
    });
  }
  combinedCommands.push({ method: 'addTextSize', params: [1, 2] });
  combinedCommands.push({ method: 'addText', params: [padAmountLine('Total', formatPrice(invoiceData.total, finalSettings.currency)) + '\n'] });
  combinedCommands.push({ method: 'addTextSize', params: [1, 1] });
  if (invoiceData.totalInDollars && invoiceData.totalInDollars > 0) {
    combinedCommands.push({
      method: 'addText',
      params: [padAmountLine('Total USD', formatPrice(invoiceData.totalInDollars, 'USD')) + '\n'],
    });
  }
  combinedCommands.push({ method: 'addText', params: [getThermalDashLine()] });

  if (invoiceData.paymentMethod?.name) {
    combinedCommands.push({ method: 'addText', params: [padAmountLine('Método de pago', invoiceData.paymentMethod.name) + '\n'] });
  }
  if (invoiceData.cashPaymentDetails) {
    const { amountReceived, changeGiven } = invoiceData.cashPaymentDetails;
    combinedCommands.push({ method: 'addText', params: [padAmountLine('Recibido', formatPrice(amountReceived, finalSettings.currency)) + '\n'] });
    combinedCommands.push({ method: 'addText', params: [padAmountLine('Cambio', formatPrice(changeGiven, finalSettings.currency)) + '\n'] });
  }
  combinedCommands.push({ method: 'addText', params: [getThermalSeparator()] });
  combinedCommands.push({ method: 'addFeedLine', params: [1] });
  combinedCommands.push({ method: 'addTextAlign', params: ['center'] });
  combinedCommands.push({ method: 'addText', params: ['Gracias por su visita!'] });
  combinedCommands.push({ method: 'addFeedLine', params: [2] });
  combinedCommands.push({ method: 'addTextFont', params: ['font_b'] });
  combinedCommands.push({ method: 'addText', params: ['Powered by Hermosa Software'] });
  appendTrailingFeed(combinedCommands);
  const cashPaymentMethods = [COLONES_PAYMENT_METHOD, DOLLAR_PAYMENT_METHOD];
  const isCashPaymentMethod = cashPaymentMethods.includes(invoiceData.paymentMethod?.defaultPaymentMethodId as number);
  if (isPaymentReceipt && isCashPaymentMethod) combinedCommands.push({ method: 'addPulse', params: [] });

  return {
    commands: combinedCommands,
    metadata: {
      type: 'tab_check', // check this
      timestamp: new Date(),
    },
  };
};

/**
 * Create test page template for Epson printer
 */
export const createTestPageTemplate = (): PrinterTemplate => {

  const commands: Array<{ method: string; params: unknown[] }> = [];

  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  commands.push({ method: 'addText', params: ['TEST PAGE'] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addText', params: ['Epson Printer Test'] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addText', params: [`Date: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addText', params: ['If you can see this, the printer is working!'] });
  appendTrailingFeed(commands);

  return {
    commands,
    metadata: {
      type: 'test_page',
      timestamp: new Date(),
    },
  };
};

// dispatchOrderTemplate here